import React, { useEffect, useRef, useState } from "react";
import PracticeLayout from "../../../components/PracticeLayout";
import PracticeSection from "../../../components/PracticeSection";
import { Button } from "../../../components/ui/button";
import useDocumentTitle from "../../../hooks/useDocumentTitle";

export default function LevelMouseKeyboard() {
  useDocumentTitle("Level 3 · Mouse & Keyboard — Practice");
  const [hoverReveal, setHoverReveal] = useState(false);
  const [doubleCount, setDoubleCount] = useState(0);
  const [singleCount, setSingleCount] = useState(0);
  const [ctx, setCtx] = useState({ open: false, x: 0, y: 0 });
  const [drag, setDrag] = useState({ todo: ["Design", "Build", "Ship"], done: [] });
  const [dragging, setDragging] = useState(null);
  const [keyLog, setKeyLog] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const target = useRef();

  useEffect(() => {
    const close = () => setCtx((c) => ({ ...c, open: false }));
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  const onDrop = (col) => (e) => {
    e.preventDefault();
    if (!dragging) return;
    setDrag((d) => {
      const cleaned = { todo: d.todo.filter((x) => x !== dragging.item), done: d.done.filter((x) => x !== dragging.item) };
      cleaned[col] = [...cleaned[col], dragging.item];
      return cleaned;
    });
    setDragging(null);
  };

  const onKey = (e) => {
    const combo = [e.ctrlKey && "Ctrl", e.altKey && "Alt", e.shiftKey && "Shift", e.metaKey && "Meta", e.key].filter(Boolean).join(" + ");
    setKeyLog((l) => [combo, ...l].slice(0, 8));
  };

  const scrollTo = () => {
    target.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <PracticeLayout
      eyebrow="Level 3 · Mouse & Keyboard"
      title="Pointer gestures & keyboard events"
      subtitle="These interactions rarely show up in the DOM until they happen — perfect for practising deliberate waits and event dispatch."
      seo={{
        title: "Level 3 · Mouse & Keyboard — Hover, Drag & Drop, Right Click",
        description: "Practise hover, double-click, right-click context menus, drag & drop, keyboard events and scroll-into-view with Selenium & Playwright.",
        keywords: "selenium hover practice, playwright drag drop, right click automation, keyboard events testing",
        image: "/og/og-mouse-keyboard.svg",
      }}
    >
      <PracticeSection id="hover" title="Hover" hint="Playwright: page.hover() · Selenium: Actions.moveToElement()">
        <div
          data-testid="hover-target"
          onMouseEnter={() => setHoverReveal(true)}
          onMouseLeave={() => setHoverReveal(false)}
          className="inline-block px-6 py-4 rounded-md bg-blue-50 border border-blue-200 cursor-pointer text-[#002FA7] font-semibold"
        >
          Hover me
        </div>
        {hoverReveal && (
          <div data-testid="hover-reveal" className="mt-3 p-3 rounded bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
            🎉 Element revealed on hover!
          </div>
        )}
      </PracticeSection>

      <PracticeSection id="double-click" title="Double click" hint="Distinguish single vs double click.">
        <div className="flex flex-wrap items-center gap-4">
          <Button
            onClick={() => setSingleCount((c) => c + 1)}
            onDoubleClick={() => setDoubleCount((c) => c + 1)}
            data-testid="dblclick-target"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white"
          >
            Single OR double-click me
          </Button>
          <span data-testid="dblclick-single-count" className="tag-mono text-sm">single: {singleCount}</span>
          <span data-testid="dblclick-double-count" className="tag-mono text-sm">double: {doubleCount}</span>
        </div>
      </PracticeSection>

      <PracticeSection id="right-click" title="Right click (context menu)" hint="Playwright: click({button:'right'})">
        <div
          onContextMenu={(e) => { e.preventDefault(); setCtx({ open: true, x: e.clientX, y: e.clientY }); }}
          data-testid="rightclick-target"
          className="p-8 rounded-md bg-zinc-100 border border-zinc-300 text-center text-sm text-zinc-700 select-none"
        >
          Right-click inside this box
        </div>
        {ctx.open && (
          <div
            data-testid="rightclick-menu"
            style={{ position: "fixed", top: ctx.y, left: ctx.x, zIndex: 60 }}
            className="bg-white border border-zinc-200 rounded shadow-lg p-1 text-sm"
          >
            {["Refresh", "Copy", "Paste"].map((a) => (
              <button
                key={a}
                data-testid={`rightclick-menu-${a.toLowerCase()}`}
                className="block w-40 text-left px-3 py-1.5 rounded hover:bg-blue-50 hover:text-[#002FA7]"
              >
                {a}
              </button>
            ))}
          </div>
        )}
      </PracticeSection>

      <PracticeSection id="drag-drop" title="Drag & drop" hint="Playwright: page.dragAndDrop(src, tgt)">
        <div className="grid sm:grid-cols-2 gap-4">
          {["todo", "done"].map((col) => (
            <div
              key={col}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop(col)}
              data-testid={`drag-col-${col}`}
              className="border-2 border-dashed border-zinc-300 rounded p-3 min-h-[120px] bg-zinc-50"
            >
              <div className="text-[10px] tag-mono uppercase tracking-widest text-zinc-500 font-bold mb-2">{col}</div>
              {drag[col].map((item) => (
                <div
                  key={item}
                  draggable
                  onDragStart={() => setDragging({ item, from: col })}
                  data-testid={`drag-item-${item}`}
                  className="bg-white border border-zinc-200 rounded px-3 py-1.5 mb-2 cursor-move text-sm shadow-sm"
                >
                  {item}
                </div>
              ))}
            </div>
          ))}
        </div>
      </PracticeSection>

      <PracticeSection id="keyboard" title="Keyboard" hint="Focus the box then type — modifier keys are logged too.">
        <div
          tabIndex={0}
          onKeyDown={onKey}
          data-testid="keyboard-target"
          className="rounded-md p-6 border border-zinc-300 bg-zinc-50 text-sm text-zinc-700 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/40"
        >
          Focus me and press keys (try Ctrl+Shift+K)
        </div>
        <ul data-testid="keyboard-log" className="mt-3 tag-mono text-[11px] text-zinc-600 space-y-0.5">
          {keyLog.map((k, i) => <li key={i}>▸ {k}</li>)}
          {keyLog.length === 0 && <li className="italic">no keys yet</li>}
        </ul>
      </PracticeSection>

      <PracticeSection id="scrolling" title="Scrolling" hint="Practice scrollIntoView() and observer-based waits.">
        <Button onClick={scrollTo} data-testid="scroll-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
          Scroll to the target
        </Button>
        <div className="mt-2 space-y-2 text-xs tag-mono text-zinc-500 h-40 overflow-y-auto p-2 border border-zinc-200 rounded bg-white" data-testid="scroll-container">
          {Array.from({ length: 40 }).map((_, i) => (
            <div key={i}>filler line {i + 1}</div>
          ))}
          <div
            ref={target}
            onFocus={() => setScrolled(true)}
            data-testid="scroll-target"
            className="p-3 mt-6 rounded bg-emerald-50 border border-emerald-200 text-emerald-800"
          >
            ✓ You reached the target
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={`b-${i}`}>trailing line {i + 1}</div>
          ))}
        </div>
      </PracticeSection>
    </PracticeLayout>
  );
}
